<template>
  <div class="modal overflow-auto" select-cy="create-user">
    <div class="card inner">
      <div class="header">
        <div class="title">
          <h2>New User</h2>
        </div>
        <div class="close" @click="close()">
          <i class="icn icn-x sm"></i>
        </div>
      </div>
      <div class="content">
        <form id="user-details" @submit.prevent @submit="create">
          <h3 class="sub-title">Details</h3>
          <div class="row mt-2">
            <div class="field">
              <input
                required
                v-model="item.firstName"
                type="text"
                placeholder="First name*"
                maxlength="127"
                autocomplete="nope"
                data-cy="first"
              />
            </div>
            <div class="field">
              <input
                required
                v-model="item.lastName"
                type="text"
                placeholder="Last name*"
                maxlength="127"
                autocomplete="nope"
                data-cy="last"
              />
            </div>
          </div>
          <div class="row mt-2">
            <div class="field">
              <input
                required
                v-model="item.email"
                type="text"
                pattern="[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                title="E.g: johndoe@example.com"
                placeholder="Email*"
                maxlength="127"
                autocomplete="nope"
                @input="onEmailInput()"
                data-cy="email"
              />
            </div>
          </div>
          <div class="row mt-2">
            <div class="field">
              <input
                required
                v-model="item.password"
                type="text"
                placeholder="Password*"
                maxlength="127"
                minlength="6"
                autocomplete="nope"
                data-cy="password"
              />
            </div>
            <div class="field">
              <label class="for-checkbox">
                <input v-model="sendResetPasswordEmail" type="checkbox" data-cy="send-email" />
                <span>Send reset password email on create.</span>
              </label>
            </div>
          </div>
          <h3 class="sub-title" v-if="canEditSuperAdmin">Access</h3>
          <div class="row mt-2" v-if="canEditSuperAdmin">
            <div class="field">
              <Toggle ref="toggleAdmin" label="Is super admin" :default="false"></Toggle>
            </div>
          </div>
          <h3 class="sub-title">Company</h3>
          <div class="row mt-2">
            <div class="field">
              <select required v-model="item.companyId">
                <option :value="c.id" :selected="c.id === item.companyId" v-for="c of companies" :key="c.id">
                  {{ c.name }}
                </option>
              </select>
            </div>
          </div>
        </form>
      </div>

      <!-- Error Messages -->
      <Alert v-if="errorMessage" :message="errorMessage"></Alert>

      <div class="footer">
        <button class="secondary" type="button" @click="close()">Cancel</button>
        <div class="flex-grow"></div>
        <button type="submit" @click="create()" :disabled="!canSubmit">
          <span v-if="!isValidating">Create & close</span>
          <span class="icon" v-if="isValidating">
            <i class="icn icn-refresh sm animate-reverse-spin"></i>
          </span>
        </button>
        <button type="submit" @click="createAndView()" :disabled="!canSubmit">
          <span v-if="!isValidating">Create & view</span>
          <span class="icon" v-if="isValidating">
            <i class="icn icn-refresh sm animate-reverse-spin"></i>
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { MODAL_NAME } from './../store';
import { ALERT_TYPE, MESSAGES } from '@/helpers/constants';
import Toggle from '@/components/Toggle';
import Alert from '@/components/Alert.vue';
import firebase from 'firebase/app';

export default {
  name: 'CompanyCreate',
  components: {
    Alert,
    Toggle,
  },

  data() {
    return {
      item: {
        firstName: '',
        lastName: '',
        email: '',
        companyId: null,
        // Will force the user to be set at enabled even if we re-activate a deleted one
        isEnabled: true,
      },
      companies: [],
      canCreate: true,
      sendResetPasswordEmail: true,
      isValidating: false,
      message: null,
      checkEmail: {
        // eslint-disable-next-line
        emailRegex: /.+\@.+\..+/,
        lastTimestamp: Date.now(),
        isLoading: false,
        timeout: null,
        delay: 300,
      },
    };
  },
  computed: {
    currentUser() {
      return this.$store.getters['auth/currentUser'];
    },
    canEditSuperAdmin() {
      return this.currentUser && this.currentUser.isSuperAdmin && this.currentUser.id != this.item.id; // You cannot change your own status
    },
    canSubmit() {
      return this.canCreate && !this.checkEmail.isLoading;
    },
    errorMessage: {
      get() {
        return this.message;
      },
      set(value) {
        if (value === null) {
          this.message = null;
        } else {
          this.message = {
            content: value,
            type: ALERT_TYPE.ERROR,
          };
        }
      },
    },
  },

  created() {
    this.fetchCompanies();
  },

  methods: {
    close() {
      this.$store.commit('users/close', MODAL_NAME.CREATE);
    },
    fetchCompanies() {
      this.$store.dispatch('users/getCompanies').then(({ data }) => {
        this.companies = data;
        if (data.length > 1) {
          this.item.companyId = data[0].id;
        }
      });
    },
    async createAndView() {
      try {
        const item = await this.save();
        if (item) {
          this.$router.push(`users/${item.id}`);
          this.close();
        }
      } catch (err) {
        console.error('err', err);
      }
    },
    async create() {
      try {
        await this.save();
        this.close();
      } catch (err) {
        console.error('err', err);
      }
    },
    async save() {
      if (this.isValidating) return;
      this.errorMessage = null;
      this.isValidating = true;

      const isValid = document.querySelector('#user-details').reportValidity();
      if (isValid) {
        if (this.$refs.toggleAdmin && this.canEditSuperAdmin) this.item.isSuperAdmin = this.$refs.toggleAdmin.get();
        this.item.firstName = this.item.firstName.trim();
        this.item.lastName = this.item.lastName.trim();

        // Trim values
        this.item.firstName = this.item.firstName.trim();
        this.item.lastName = this.item.lastName.trim();
        this.item.email = this.item.email.trim();

        return this.$store
          .dispatch('users/create', [this.item])
          .then(data => {
            this.$store.dispatch('notifications/success', {
              headline: 'New User',
              details: 'The user has been successfully created.',
            });

            if (this.sendResetPasswordEmail) {
              return firebase
                .auth()
                .sendPasswordResetEmail(this.item.email)
                .then(() => {
                  this.$store.dispatch('notifications/success', {
                    headline: 'New User',
                    details: 'The reset password email has been sent.',
                  });
                  return data[0].data;
                })
                .catch(() => {
                  this.$store.dispatch('notifications/error', {
                    headline: 'New User',
                    details: "We couldn't send the reset password email. Please edit the user and send it from there.",
                  });
                });
            } else {
              return data[0].data;
            }
          })
          .catch(err => {
            console.error(err);
            if (err && err.status && err.status === 401) {
              this.errorMessage = MESSAGES.NO_PERMISSION;
            } else {
              this.errorMessage = MESSAGES.SERVER_ERROR;
            }
            throw err;
          })
          .finally(() => (this.isValidating = false));
      } else {
        this.isValidating = false;
        throw 'Not valid';
      }
    },
    onEmailInput() {
      clearTimeout(this.checkEmail.timeout);
      this.checkEmail.timeout = setTimeout(() => {
        this.validateEmail();
      }, this.checkEmail.delay);
    },
    validateEmail() {
      if (this.item.email.length > 0 && this.checkEmail.emailRegex.test(this.item.email)) {
        let timestamp = Date.now(); // timestamp for this api call
        this.checkEmail.lastTimestamp = timestamp; // 1 , 2 , 3
        this.checkEmail.isLoading = true;

        this.$store
          .dispatch('users/search', { key: 'email', value: this.item.email })
          .then(({ data }) => {
            if (data && timestamp >= this.checkEmail.lastTimestamp) {
              if (data.length > 0) {
                this.errorMessage = 'Emails must be unique. A user with the same email already exists.';
                this.canCreate = false;
              } else {
                this.errorMessage = null;
                this.canCreate = true;
              }
            }
          })
          .catch(err => {
            console.error(err);
            // If it fails, let the user free to continue
            this.errorMessage = null;
            this.canCreate = true;
          })
          .finally(() => (this.checkEmail.isLoading = false));
      } else {
        this.errorMessage = null;
        this.canCreate = true;
      }
    },
  },
};
</script>
